import { HTML_BLOCK_CSP_DIRECTIVES } from '../../consts/html-block-policy'
import { HTML_BLOCK_REPORTER_SOURCE } from './frame-reporter'

const HTML_BLOCK_TITLE = 'Rendered HTML content'

let nonceSequence = 0

const createNonce = (): string => {
  try {
    const randomUUID = globalThis.crypto?.randomUUID
    if (typeof randomUUID === 'function') {
      return randomUUID.call(globalThis.crypto)
    }
  } catch {
    // Fall through to the local fallback for non-secure or restricted contexts.
  }

  nonceSequence += 1
  return `html-block-${Date.now().toString(36)}-${nonceSequence.toString(36)}`
}

const createCsp = (nonce: string): string => {
  return [
    ...HTML_BLOCK_CSP_DIRECTIVES.slice(0, 4),
    `script-src 'nonce-${nonce}'`,
    ...HTML_BLOCK_CSP_DIRECTIVES.slice(4),
  ].join('; ')
}

const createInjectedStyle = (hasMeasuredRoot: boolean): string => {
  const rootStyle = hasMeasuredRoot ? '#__max_root { min-height: 0; }' : ''
  const flattenSelector = hasMeasuredRoot ? '#__max_root > *, body > *' : 'body > *'
  const cardPaddingSelector = hasMeasuredRoot
    ? '#__max_root > :only-child, body > :only-child'
    : 'body > :only-child'

  return `
html, body { margin: 0; height: auto; overflow: hidden; }
${rootStyle}
@page { margin: 12mm; }
@media print {
  html, body { height: auto; overflow: visible; }

  ${flattenSelector} {
    box-shadow: none !important;
    border-radius: 0 !important;
    max-width: none !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  ${cardPaddingSelector} {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  h1, h2, h3, h4 { break-after: avoid-page; }
  table, figure, blockquote, pre, img { break-inside: avoid; }
  tr, li { break-inside: avoid; }
}
`
}

const createElement = (root: HTMLHtmlElement, tagName: string): HTMLElement => {
  return root.ownerDocument.createElement(tagName)
}

const appendCspMeta = (root: HTMLHtmlElement, head: HTMLElement, nonce: string): void => {
  const cspMeta = createElement(root, 'meta')
  cspMeta.setAttribute('http-equiv', 'Content-Security-Policy')
  cspMeta.setAttribute('content', createCsp(nonce))
  head.prepend(cspMeta)
}

const ensureCharset = (root: HTMLHtmlElement, head: HTMLElement): void => {
  if (head.querySelector('meta[charset]')) {
    return
  }

  const charset = createElement(root, 'meta')
  charset.setAttribute('charset', 'utf-8')
  head.append(charset)
}

const appendStyle = (root: HTMLHtmlElement, head: HTMLElement, hasMeasuredRoot: boolean): void => {
  const style = createElement(root, 'style')
  style.textContent = createInjectedStyle(hasMeasuredRoot)
  head.append(style)
}

const ensureTitle = (root: HTMLHtmlElement, head: HTMLElement): void => {
  if (head.querySelector('title')) {
    return
  }

  const title = createElement(root, 'title')
  title.textContent = HTML_BLOCK_TITLE
  head.append(title)
}

const wrapFragmentBody = (root: HTMLHtmlElement, body: HTMLElement): void => {
  const measuredRoot = createElement(root, 'div')
  measuredRoot.id = '__max_root'

  while (body.firstChild) {
    measuredRoot.append(body.firstChild)
  }

  body.append(measuredRoot)
}

/**
 * Append the reporter verbatim. The text is never parameterised: its SHA-256 is allowlisted
 * in the host CSP, which a srcdoc document inherits. The frame's id reaches it through the
 * iframe `name` attribute (`window.name`) instead.
 */
const appendReporter = (root: HTMLHtmlElement, body: HTMLElement, nonce: string): void => {
  const reporter = createElement(root, 'script')
  reporter.setAttribute('nonce', nonce)
  reporter.textContent = HTML_BLOCK_REPORTER_SOURCE
  body.append(reporter)
}

/**
 * Build the isolated document loaded by a rendered HTML block iframe.
 * The caller supplies the one DOMPurify-produced document root. The root is
 * cloned before document-owned elements are injected so repeated builds do
 * not accumulate CSP, style, or reporter elements on the sanitized input.
 */
export const buildSrcDoc = (root: HTMLHtmlElement, fullDocument: boolean): string => {
  const documentRoot = root.cloneNode(true) as HTMLHtmlElement
  const nonce = createNonce()
  const head = documentRoot.querySelector('head')
  const body = documentRoot.querySelector('body')

  if (!head || !body) {
    throw new Error('Sanitized HTML document has no head or body element')
  }

  appendCspMeta(documentRoot, head, nonce)
  if (!fullDocument) {
    ensureCharset(documentRoot, head)
  }
  ensureTitle(documentRoot, head)
  appendStyle(documentRoot, head, !fullDocument)

  if (!fullDocument) {
    wrapFragmentBody(documentRoot, body)
  }

  appendReporter(documentRoot, body, nonce)

  const doctype = !fullDocument || documentRoot.ownerDocument.doctype ? '<!doctype html>' : ''
  return `${doctype}${documentRoot.outerHTML}`
}
